/**
 * Himalume Section 1 product-page foundation script.
 * Vanilla JS only -- no libraries. Handles: variant selection (price /
 * compare-at / availability / URL / sticky-bar / gallery-media sync) and
 * the quantity stepper. Gallery thumbnail interaction lives in
 * himalume-product-gallery.js.
 *
 * Scoped per section instance (a page can only have one main product
 * section today, but this is written defensively so it still works
 * correctly if that ever changes).
 */
(function () {
  'use strict';

  function initHimalumeProductForm(root) {
    var dataEl = root.querySelector('[data-himalume-variant-data]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent).variants;
    } catch (e) {
      return;
    }

    var variantIdInput = root.querySelector('[data-himalume-variant-id]');
    var priceEl = root.querySelector('[data-himalume-price]');
    var comparePriceEl = root.querySelector('[data-himalume-compare-price]');
    var priceBadgeEl = root.querySelector('[data-himalume-price-badge]');
    var atcButton = root.querySelector('[data-himalume-atc-button]');
    var atcLabel = root.querySelector('[data-himalume-atc-label]');
    var dynamicCheckout = root.querySelector('[data-himalume-dynamic-checkout]');
    var optionInputs = root.querySelectorAll('[data-himalume-option-input]');
    var stickyForm = document.querySelector('.hsl-sticky-atc form');
    var stickyIdInput = stickyForm ? stickyForm.querySelector('input[name="id"]') : null;
    var stickyAtcButton = stickyForm ? stickyForm.querySelector('button[type="submit"]') : null;
    var stickyPriceEl = document.querySelector('.hsl-sticky-atc-price');

    function currentSelection() {
      var selected = [];
      var groups = {};
      optionInputs.forEach(function (input) {
        if (input.checked) {
          groups[input.dataset.optionPosition] = input.value;
        }
      });
      var positions = Object.keys(groups).map(Number).sort(function (a, b) { return a - b; });
      positions.forEach(function (pos) { selected.push(groups[pos]); });
      return selected;
    }

    function findMatchingVariant(selection) {
      if (selection.length === 0) return variants[0];
      return variants.find(function (v) {
        return v.options.length === selection.length && v.options.every(function (val, i) { return val === selection[i]; });
      });
    }

    function updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    function updateGalleryMedia(variant) {
      if (!variant || !variant.featuredMediaId) return;
      var gallery = root.querySelector('[data-himalume-gallery]');
      if (!gallery) return;
      var track = gallery.querySelector('[data-himalume-gallery-track]');
      var itemList = Array.prototype.slice.call(gallery.querySelectorAll('[data-himalume-gallery-item]'));
      var targetIndex = -1;
      itemList.forEach(function (item, i) {
        if (item.dataset.mediaId === String(variant.featuredMediaId)) targetIndex = i;
      });
      if (targetIndex < 0) return;

      // Slide the scroll-snap track to the variant's featured media.
      if (track && itemList[targetIndex]) {
        track.scrollTo({ left: itemList[targetIndex].offsetLeft, behavior: 'smooth' });
      }
      itemList.forEach(function (item, i) {
        item.classList.toggle('is-active', i === targetIndex);
      });
      gallery.querySelectorAll('[data-himalume-gallery-thumb]').forEach(function (thumb, i) {
        var isTarget = i === targetIndex;
        thumb.classList.toggle('is-active', isTarget);
        thumb.setAttribute('aria-selected', isTarget ? 'true' : 'false');
      });
    }

    function applyVariant(variant) {
      if (!variant) return;

      if (variantIdInput) variantIdInput.value = variant.id;
      if (stickyIdInput) stickyIdInput.value = variant.id;
      if (stickyAtcButton) {
        stickyAtcButton.disabled = !variant.available;
        stickyAtcButton.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }

      if (priceEl) priceEl.textContent = variant.price;
      if (stickyPriceEl) stickyPriceEl.textContent = variant.price;

      if (comparePriceEl && priceBadgeEl) {
        if (variant.compareAtPrice) {
          comparePriceEl.textContent = variant.compareAtPrice;
          comparePriceEl.hidden = false;
          priceBadgeEl.hidden = false;
        } else {
          comparePriceEl.hidden = true;
          priceBadgeEl.hidden = true;
        }
      }

      if (atcButton) atcButton.disabled = !variant.available;
      if (atcLabel) atcLabel.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      if (dynamicCheckout) dynamicCheckout.hidden = !variant.available;

      updateGalleryMedia(variant);
      updateUrl(variant);
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var variant = findMatchingVariant(currentSelection());
        applyVariant(variant);
      });
    });

    // Quantity stepper
    var qtyInput = root.querySelector('[data-himalume-qty-input]');
    var qtyDecrease = root.querySelector('[data-himalume-qty-decrease]');
    var qtyIncrease = root.querySelector('[data-himalume-qty-increase]');
    if (qtyInput && qtyDecrease && qtyIncrease) {
      var step = function () { return parseInt(qtyInput.step, 10) || 1; };
      var min = function () { return parseInt(qtyInput.min, 10) || 1; };
      var max = function () { return qtyInput.max ? parseInt(qtyInput.max, 10) : Infinity; };
      qtyDecrease.addEventListener('click', function () {
        var next = (parseInt(qtyInput.value, 10) || min()) - step();
        qtyInput.value = Math.max(min(), next);
      });
      qtyIncrease.addEventListener('click', function () {
        var next = (parseInt(qtyInput.value, 10) || min()) + step();
        qtyInput.value = Math.min(max(), next);
      });
    }
  }

  var roots = document.querySelectorAll('[data-himalume-product-root]');
  if (roots.length) {
    // The theme sets `overflow-y: auto` on both <html> and <body>, which makes
    // <body> -- not <html>, the element that actually scrolls -- the nearest
    // scrolling ancestor for position:sticky's containing-block calculation,
    // silently breaking sticky positioning for any descendant. Scoping this
    // correction to the product page only (never touching layout/theme.liquid)
    // lets the gallery column's sticky rule actually take effect on desktop.
    document.body.classList.add('hsl-product-page-active');
  }
  roots.forEach(initHimalumeProductForm);
})();
